import { useState, useEffect, useRef } from 'react';
import { Modal } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { toggleModal } from '../../reducers/modalReducer';
import { postRequest, showMessage } from '../../config';
import { SHOW_AI_FEATURES } from '../../env';
import './autoFill.css';


const VERDICT_STYLES = {
    correct:   { bg: '#ECFDF5', border: '#A7F3D0', color: '#059669', label: 'Correct' },
    incorrect: { bg: '#FFF5F7', border: '#FCA5A5', color: '#DC2626', label: 'Incorrect' },
};

const extractSuggestedQNo = (reason) => {
    if (!reason) return null;
    const match = reason.match(/under\s+([A-Z0-9]+(?:\.[0-9]+(?:\.[0-9]+)*)?)\s*:/i) || reason.match(/belongs to\s+([A-Z0-9]+(?:\.[0-9]+(?:\.[0-9]+)*)?)\s*:/i);
    return match ? match[1] : null;
};

const DiagnoseReportModal = () => {
    const dispatch = useDispatch();
    const modal = useSelector(state => state.modal);
    const isOpen = modal.open === 'diagnoseReport';
    const loading = !!modal.diagnoseLoading;
    const results = modal.diagnoseResults || [];
    const summary = modal.diagnoseSummary || null;
    const moduleName = modal.diagnoseModuleName || '';

    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [fixedCards, setFixedCards] = useState(new Set());
    const [fixingCards, setFixingCards] = useState(new Set());
    const [applyingAll, setApplyingAll] = useState(false);

    useEffect(() => { if (!isOpen) { setFilter('all'); setSearch(''); setFixedCards(new Set()); setFixingCards(new Set()); setApplyingAll(false); } }, [isOpen]);

    const handleApplyFix = async (r, suggestedQNo) => {
        setFixingCards(prev => new Set(prev).add(r.qNo));
        try {
            const response = await postRequest('/clarence/apply-fix', { companyId: modal.diagnoseCompanyId, originalQNo: r.qNo, suggestedQNo, moduleType: modal.diagnoseModuleType, ...(modal.diagnoseModuleId ? { moduleId: modal.diagnoseModuleId } : {}), reason: r.reason || '', });
            if (response?.code === 200) {
                setFixedCards(prev => new Set(prev).add(r.qNo));
                dispatch(toggleModal({ diagnoseFixJustApplied: true }));
            } else showMessage(response?.message || 'Could not apply fix. Please try again.');
        } catch (_) {
            showMessage('Could not apply fix. Please try again.');
        }
        setFixingCards(prev => { const s = new Set(prev); s.delete(r.qNo); return s; });
    };

    const handleApplyAllFixes = async () => {
        const pending = results.filter(r => r.suggestedQNo && !fixedCards.has(r.qNo) && (r.files || []).length > 0);
        if (!pending.length) return;
        setApplyingAll(true);
        await Promise.all(pending.map(r => handleApplyFix(r, r.suggestedQNo)));
        setApplyingAll(false);
    };

    const filtered = results.filter(r => {
        const matchFilter = filter === 'all' || r.verdict === filter;
        const matchSearch = !search || r.question?.toLowerCase().includes(search.toLowerCase()) || r.qNo?.toLowerCase().includes(search.toLowerCase());
        return matchFilter && matchSearch;
    });

    const close = () => dispatch(toggleModal({ open: '' }));

    return (
        <Modal open={isOpen} onClose={close} className="backdrop td500 zi4 pf t0 l0 w100vw h100vh df jcc aic">
            <div className="pr shadow df fdc bg1 br10px" style={{ width: '95vw', maxWidth: '1450px', maxHeight: '90vh', overflow: 'hidden' }}>

                {/* Header */}
                <div className="df aic jcsb px2rem py1rem" style={{ borderBottom: '1px solid #F3F4F6', flexShrink: 0 }}>
                    <div className="df aic" style={{ gap: '0.75rem' }}>
                        <div className="df aic jcc br10px" style={{ width: 40, height: 40, background: '#EEF2FF' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#4A67FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div>
                            <p className="c15 fw600 fs1-125rem mb0rem">Auditor Report</p>
                            {moduleName && <p className="c12 fs13px fw400 mb0rem">{moduleName}</p>}
                        </div>
                    </div>
                    <button onClick={close} className="cp bg5-hover br100 df aic jcc" style={{ width: 32, height: 32, border: 'none', background: 'none' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" /></svg>
                    </button>
                </div>

                {/* Loading state */}
                {loading && (
                    <div className="df fdc aic jcc" style={{ flex: 1, paddingBottom: '3rem' }}>
                        <img src={require('../../../images/wizard.gif')} alt="processing" style={{ width: '220px', marginBottom: '1.5rem' }} />
                        <p style={{ fontSize: '18px', fontWeight: 600, color: '#151313', textAlign: 'center', margin: '0 0 0.5rem 0' }}>Clarence is verifying answers…</p>
                        <p style={{ fontSize: '13px', fontWeight: 400, color: '#9CA3AF', textAlign: 'center', margin: '0 0 1.5rem 0' }}>Checking each answer against your uploaded documents — this may take a few minutes</p>
                    </div>
                )}

                {/* Results */}
                {!loading && (
                    <div className="df fdc" style={{ flex: 1, overflow: 'hidden' }}>

                        {/* Summary bar */}
                        {summary && (
                            <div className="df aic px2rem py1rem" style={{ gap: '1rem', borderBottom: '1px solid #F3F4F6', flexShrink: 0 }}>
                                {[
                                    { label: 'Total', value: summary.total, bg: '#F9FAFB', color: '#374151', border: '#E5E7EB', showPct: false },
                                    { label: 'Correct', value: summary.correct, bg: '#ECFDF5', color: '#059669', border: '#A7F3D0', showPct: true },
                                    { label: 'Incorrect', value: summary.incorrect, bg: '#FFF5F7', color: '#DC2626', border: '#FCA5A5', showPct: true },
                                ].map(({ label, value, bg, color, border, showPct }) => {
                                    const pct = showPct && summary.total > 0 ? Math.round((value / summary.total) * 100) : null;
                                    return (
                                        <div key={label} className="df fdc aic jcc br10px" style={{ background: bg, border: `1px solid ${border}`, flex: 1, padding: '1rem' }}>
                                            <span style={{ fontSize: '1.75rem', fontWeight: 700, color, lineHeight: 1.1 }}>{value ?? 0}</span>
                                            <span style={{ fontSize: '13px', fontWeight: 500, color, marginTop: '4px' }}>{label}{pct !== null ? <span style={{ fontWeight: 400, opacity: 0.75 }}> ({pct}%)</span> : null}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Search + filter */}
                        {results.length > 0 && (
                            <div className="df aic px2rem py0-75rem" style={{ gap: '0.5rem', borderBottom: '1px solid #F3F4F6', flexShrink: 0, flexWrap: 'wrap' }}>
                                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search questions…" className="fs13px fw400 c15 br50px bss bw1px bo29 px1rem py0-5rem" style={{ outline: 'none', flex: '1 1 200px', minWidth: 0 }} />
                                <div className="df aic" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
                                    {[{ key: 'all', label: 'All' }, { key: 'correct', label: 'Correct' }, { key: 'incorrect', label: 'Incorrect' }].map(({ key, label }) => (
                                        <button key={key} onClick={() => setFilter(key)} className="br50px px1rem py0-375rem fs13px cp" style={{ border: '1px solid #D1D5DB', background: filter === key ? '#fff' : '#F3F4F6', color: '#151313', fontWeight: filter === key ? 700 : 400, boxShadow: filter === key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>{label}</button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Results list */}
                        <div className="oya" style={{ flex: 1, padding: '1.5rem 2rem' }}>
                            {results.length === 0 && (
                                <div className="df fdc aic jcc p3rem">
                                    <p className="c12 tac fs14px mb0rem">No answers found for the selected sources.</p>
                                </div>
                            )}

                            {filtered.length === 0 && results.length > 0 && (
                                <div className="df fdc aic jcc p2rem">
                                    <p className="c12 tac fs13px mb0rem">No results match the current filter.</p>
                                </div>
                            )}

                            {filtered.map((r, idx) => {
                                const vs = VERDICT_STYLES[r.verdict] || VERDICT_STYLES.needs_review;
                                const conf = r.confidence ?? 0;
                                const confColor = conf >= 0.85 ? { bg: '#D1FAE5', color: '#065F46', border: '#6EE7B7' } : conf >= 0.6 ? { bg: '#FEF3C7', color: '#92400E', border: '#FCD34D' } : { bg: '#FEE2E2', color: '#991B1B', border: '#FCA5A5' };
                                const suggestedQNo = r.suggestedQNo ?? null;
                                const isFixed = fixedCards.has(r.qNo);
                                const isFixing = fixingCards.has(r.qNo);
                                return (
                                    <div key={idx} style={{ marginBottom: '1rem', borderRadius: '10px', background: '#fff', borderLeft: `4px solid ${vs.color}`, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', padding: '1rem 1.5rem' }}>

                                        {/* Top row: qNo + question + verdict */}
                                        <div className="df aic jcsb" style={{ gap: '0.75rem', marginBottom: '0.75rem' }}>
                                            <div className="df aic" style={{ gap: '0.5rem', flex: 1, minWidth: 0 }}>
                                                {r.qNo && <span style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{r.qNo}</span>}
                                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#151313', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.question}>{r.question}</span>
                                            </div>
                                            <span style={{ fontSize: '12px', fontWeight: 600, background: vs.bg, color: vs.color, border: `1px solid ${vs.border}`, padding: '4px 14px', borderRadius: '50px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                {vs.label}
                                            </span>
                                        </div>

                                        {/* Answer section */}
                                        <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px 14px', marginBottom: '0.75rem' }}>
                                            <div className="df aic jcsb" style={{ marginBottom: '6px' }}>
                                                <div className="df aic" style={{ gap: '0.5rem' }}>
                                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Answer</span>
                                                    <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 10px', borderRadius: '50px', ...(r.source === 'clarence' ? { background: '#EEF2FF', color: '#4A67FF', border: '1px solid #C7D2FE' } : { background: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA' }) }}>
                                                        {r.source === 'clarence' ? 'Clarence' : 'Human'}
                                                    </span>
                                                </div>
                                                <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 12px', borderRadius: '50px', background: confColor.bg, color: confColor.color, border: `1px solid ${confColor.border}` }}>
                                                    {Math.round(conf * 100)}% confidence
                                                </span>
                                            </div>
                                            <p style={{ fontSize: '15px', fontWeight: 600, color: '#151313', margin: 0 }}>{r.answer || '—'}</p>
                                        </div>

                                        {/* Reasoning */}
                                        {r.reason && (
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '4px' }}>Reasoning</span>
                                                <p style={{ fontSize: '13px', fontWeight: 400, color: '#5E5F66', lineHeight: 1.65, margin: 0 }}>{r.reason}</p>
                                            </div>
                                        )}

                                        {/* Files + Move button row */}
                                        <div className="df aic jcsb" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
                                            {(r.files || []).length > 0 ? (
                                                <div className="df aic" style={{ gap: '0.4rem', flexWrap: 'wrap', flex: 1 }}>
                                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: '2px' }}>Sources</span>
                                                    {r.files.map((f, fi) => {
                                                        const url = r.fileUrls?.[fi];
                                                        const chipStyle = { fontSize: '10px', fontWeight: 500, background: '#EFF6FF', color: '#2563EB', padding: '2px 7px', borderRadius: '5px', border: '1px solid #BFDBFE', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '3px', ...(url ? { cursor: 'pointer' } : {}) };
                                                        const inner = (<>{url && <svg width="9" height="9" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}><path d="M4 4h5v2H6v8h8v-3h2v5H4V4zm7-1h6v6h-2V6.414l-5.293 5.293-1.414-1.414L13.586 5H11V3z" fill="currentColor" /></svg>}{f}</>);
                                                        return url ? <a key={fi} href={url} target="_blank" rel="noopener noreferrer" style={chipStyle} title={f}>{inner}</a> : <span key={fi} style={chipStyle} title={f}>{inner}</span>;
                                                    })}
                                                </div>
                                            ) : <div style={{ flex: 1 }} />}

                                            {suggestedQNo && (r.files || []).length > 0 && (
                                                isFixed  ? <span style={{ fontSize: '12px', fontWeight: 600, color: '#059669', background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '50px', padding: '5px 14px', whiteSpace: 'nowrap', flexShrink: 0 }}>✓ Moved to {suggestedQNo}</span>
                                                    : <button disabled={isFixing} onClick={() => handleApplyFix(r, suggestedQNo)} style={{ fontSize: '12px', fontWeight: 600, background: '#4A67FF', color: '#fff', border: 'none', borderRadius: '50px', padding: '5px 16px', cursor: isFixing ? 'default' : 'pointer', opacity: isFixing ? 0.6 : 1, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                        {isFixing ? 'Moving…' : `Move to ${suggestedQNo}`}
                                                    </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div className="df aic jcfe px2rem py1rem" style={{ borderTop: '1px solid #F3F4F6', flexShrink: 0, gap: '0.75rem' }}>
                            {results.filter(r => r.suggestedQNo && !fixedCards.has(r.qNo) && (r.files || []).length > 0).length > 0 && (
                                <button disabled={applyingAll} onClick={handleApplyAllFixes} className="br50px px2rem py0-75rem cp fw600 fs14px c1" style={{ border: 'none', background: applyingAll ? '#6B8BFF' : '#4A67FF', opacity: applyingAll ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    {applyingAll
                                        ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" strokeDasharray="40" strokeDashoffset="10" strokeLinecap="round" /></svg>Applying…</>
                                        : <>⚡ Apply All Fixes</>
                                    }
                                </button>
                            )}
                            <button onClick={close} className="bg4 c1 br50px px3rem py0-75rem cp fw500 fs14px" style={{ border: 'none' }}>Done</button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

const DiagnoseButton = ({ companyId, moduleType, moduleId, tab, activeModule }) => {
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [selection, setSelection] = useState(new Set(['clarence', 'human']));
    const [loading, setLoading] = useState(false);
    const panelRef = useRef(null);

    useEffect(() => {
        const handleMouseDown = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, []);

    const toggleSelection = (key) => {
        setSelection(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const handleGenerateReport = async () => {
        setOpen(false);
        setLoading(true);
        dispatch(toggleModal({ open: 'diagnoseReport', diagnoseLoading: true, diagnoseResults: [], diagnoseSummary: null, diagnoseModuleName: activeModule?.heading || tab || '', diagnoseCompanyId: companyId, diagnoseModuleType: moduleType, diagnoseModuleId: moduleId || null }));
        try {
            const response = await postRequest('/clarence/diagnose', { companyId, moduleType, ...(moduleId ? { moduleId } : {}), sources: [...selection] });
            if (response?.code === 200) dispatch(toggleModal({ diagnoseLoading: false, diagnoseResults: response.body.results || [], diagnoseSummary: response.body.summary || null }));
             else {
                dispatch(toggleModal({ open: '', diagnoseLoading: false }));
                showMessage(response?.message || 'Auditor failed. Please try again.');
            }
        } catch (_) {
            dispatch(toggleModal({ open: '', diagnoseLoading: false }));
            showMessage('Auditor failed. Please try again.');
        }
        setLoading(false);
    };

    if (!SHOW_AI_FEATURES) return null;

    return (
        <div className="pr ml0-5rem" ref={panelRef}>
            <div className="auto-fill-icon-wrap">
                <button onClick={() => setOpen(o => !o)} disabled={loading} className="cp fs13px fw600 bg1 bg3-hover c3 c1-hover bw1px bo3 bss br50px px1rem py0-5rem df jcc aic">
                    {loading
                        ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite', marginRight: '6px' }}><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" strokeDasharray="40" strokeDashoffset="10" strokeLinecap="round" /></svg>
                        : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: '6px' }}><path d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    }
                    {loading ? 'Analysing...' : 'Auditor'}
                </button>
                <span className="auto-fill-hover-chip">Get an AI-powered quality check on your submitted answers</span>
            </div>

            {open && (
                <div className="pa bg1 shadow br10px zi10" style={{ minWidth: '220px', top: '110%', right: 0 }}>
                    <label className="df aic px1rem py0-5rem bg5-hover cp" style={{ gap: '0.5rem', borderTopLeftRadius: '10px', borderTopRightRadius: '10px' }}>
                        <span className="checkbox w20px h20px pr">
                            <input type="checkbox" className="bg1 bo4 bw1px bss cp td250 w20px h20px br5px" checked={selection.has('clarence')} onChange={() => toggleSelection('clarence')} />
                        </span>
                        <span className="fs13px fw400 c14">Clarence Answers</span>
                        <span className="fs11px c12 mla wsn">(AI-filled)</span>
                    </label>

                    <label className="df aic px1rem py0-5rem bg5-hover cp" style={{ gap: '0.5rem' }}>
                        <span className="checkbox w20px h20px pr">
                            <input type="checkbox" className="bg1 bo4 bw1px bss cp td250 w20px h20px br5px" checked={selection.has('human')} onChange={() => toggleSelection('human')} />
                        </span>
                        <span className="fs13px fw400 c14">Human Answers</span>
                    </label>

                    <div className="bss bs1px bo29 mx1rem" />

                    <button disabled={selection.size === 0} onClick={handleGenerateReport} className="w100 bg3 c1 fw600 fs13px cp py0-75rem" style={{ border: 'none', borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px', opacity: selection.size === 0 ? 0.5 : 1 }}>Generate Report</button>
                </div>
            )}
        </div>
    );
};

export { DiagnoseReportModal };
export default DiagnoseButton;
